// Press-release archive — formal first-party announcements only.
import { createFileRoute, Link } from "@tanstack/react-router";
import { useEffect } from "react";
import { getPressReleases, NEWSROOM_DISCLOSURE } from "@/lib/news";
import { SITE_URL } from "@/lib/site";
import { track } from "@/lib/analytics";
import {
  PORTRAIT_ALT,
  PORTRAIT_URL,
  PRESS_RELEASES_ID,
  breadcrumbs,
  itemList,
  ldScript,
  orgRef,
  personRef,
  seoMeta,
  websiteRef,
} from "@/lib/schema";

const TITLE = "Buddha KA Press Releases | Official Announcements";
const DESCRIPTION =
  "Archive of official Buddha KA and KAfrequency press releases — release announcements, framework statements, and project updates, published first-party.";

export const Route = createFileRoute("/press/releases/")({
  component: PressReleaseArchive,
  head: () => ({
    ...seoMeta({
      title: TITLE,
      description: DESCRIPTION,
      path: "/press/releases",
      image: PORTRAIT_URL,
      imageAlt: PORTRAIT_ALT,
    }),
    links: [
      { rel: "canonical", href: `${SITE_URL}/press/releases` },
      {
        rel: "alternate",
        type: "application/rss+xml",
        title: "Buddha KA press releases (RSS)",
        href: `${SITE_URL}/press/releases.xml`,
      },
    ],
    scripts: [
      ldScript({
        "@context": "https://schema.org",
        "@type": "CollectionPage",
        "@id": PRESS_RELEASES_ID,
        name: "Buddha KA Press Releases",
        description: DESCRIPTION,
        url: `${SITE_URL}/press/releases`,
        inLanguage: "en",
        isPartOf: websiteRef(),
        about: [personRef(), orgRef()],
        publisher: orgRef(),
        mainEntity: itemList(
          getPressReleases().map((n) => ({ path: `/news/${n.slug}`, name: n.title })),
        ),
      }),
      ldScript(breadcrumbs([["Press kit", "/press"], ["Press releases", "/press/releases"]])),
    ],
  }),
});

function PressReleaseArchive() {
  const releases = getPressReleases();
  useEffect(() => {
    track("press_archive_view", { count: releases.length });
  }, [releases.length]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 sm:py-24">
      <header className="mb-12">
        <p className="text-xs uppercase tracking-[0.3em] text-gold">Official Newsroom</p>
        <h1 className="mt-3 font-serif text-4xl sm:text-5xl">Press Releases</h1>
        <p className="mt-4 max-w-2xl text-muted-foreground">{DESCRIPTION}</p>
        <p className="mt-4 max-w-2xl rounded-xl border hairline bg-obsidian/60 p-4 text-sm text-muted-foreground">
          {NEWSROOM_DISCLOSURE}
        </p>
        <nav aria-label="Press destinations" className="mt-6 flex flex-wrap gap-2 text-sm">
          <Link to="/press" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Full press kit
          </Link>
          <Link to="/news" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            All newsroom stories
          </Link>
          <a
            href="/press/releases.xml"
            onClick={() => track("press_resource_click", { resource: "press-releases-rss" })}
            className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold"
          >
            RSS feed
          </a>
          <Link to="/contact" className="rounded-full border hairline px-4 py-2 hover:border-gold hover:text-gold">
            Media inquiries
          </Link>
        </nav>
      </header>

      {releases.length === 0 ? (
        <p className="rounded-2xl border hairline bg-charcoal p-8 text-center text-muted-foreground">
          No press releases have been issued yet. Official announcements will appear here.
        </p>
      ) : (
        <ol className="space-y-6">
          {releases.map((n) => (
            <li key={n.slug} className="rounded-2xl border hairline bg-charcoal p-6 transition-colors hover:border-gold">
              <p className="text-[10px] uppercase tracking-[0.3em] text-gold">
                {n.category} ·{" "}
                <time dateTime={n.publishedAt}>
                  {new Date(n.publishedAt).toLocaleDateString("en-US", {
                    year: "numeric",
                    month: "long",
                    day: "numeric",
                  })}
                </time>
              </p>
              <h2 className="mt-3 font-serif text-2xl leading-tight text-ivory">
                <Link to="/news/$slug" params={{ slug: n.slug }} className="hover:text-gold-luminous">
                  {n.title}
                </Link>
              </h2>
              <p className="mt-3 text-muted-foreground">{n.dek}</p>
              <div className="mt-4 flex flex-wrap gap-3 text-sm">
                <Link
                  to="/news/$slug"
                  params={{ slug: n.slug }}
                  className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
                >
                  Read the full release →
                </Link>
                {n.releaseSlug && (
                  <Link
                    to="/press/kit/$slug"
                    params={{ slug: n.releaseSlug }}
                    className="text-gold underline decoration-gold/40 underline-offset-4 hover:text-gold-luminous hover:decoration-gold"
                  >
                    Release press kit →
                  </Link>
                )}
              </div>
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}
